import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { useForm, Controller } from 'react-hook-form';

import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import { getFileClients } from 'services/fileManager/fileManagerServices';
import { useFileMutations } from 'modules/FileManagerV2/hooks/api/useFileMutations';
import { ModalHeader } from 'shared/ModalHeader';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import GlobalTooltip from 'components/GlobalTooltip';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';

function buildShareOperations(contactsToAdd) {
  const byItem = {};
  contactsToAdd.forEach(({ contactId, item }) => {
    const key = `${item.pk}#${item.sk}`;
    if (!byItem[key]) byItem[key] = { item, contactIds: [] };
    if (!byItem[key].contactIds.includes(contactId)) byItem[key].contactIds.push(contactId);
  });

  return Object.values(byItem).map(({ item, contactIds }) => ({
    items: {
      file_list: item.entry_type === 'file' ? [{ file_pk: item.pk, file_sk: item.sk }] : [],
      folder_list: item.entry_type === 'folder' ? [{ folder_pk: item.pk, folder_sk: item.sk }] : [],
    },
    contacts: contactIds,
  }));
}

function buildRevokeOperations(removals) {
  const byItem = {};
  removals.forEach(({ contactId, item }) => {
    const key = `${item.pk}#${item.sk}`;
    if (!byItem[key]) byItem[key] = { item, contactIds: [] };
    if (!byItem[key].contactIds.includes(contactId)) byItem[key].contactIds.push(contactId);
  });

  return Object.values(byItem).map(({ item, contactIds }) => ({
    items: {
      file_list: item.entry_type === 'file' ? [{ file_pk: item.pk, file_sk: item.sk }] : [],
      folder_list: item.entry_type === 'folder' ? [{ folder_pk: item.pk, folder_sk: item.sk }] : [],
    },
    contact_ids: contactIds,
  }));
}

function isSameContactOnItem(contact, itemDisplayName, other) {
  if (contact.display_name !== itemDisplayName) return false;

  const contactIds = [contact.contact_id, contact.value].filter(Boolean);
  const otherIds = [other?.contact_id, other?.value, other?.contactId].filter(Boolean);

  return contactIds.some((id) => otherIds.includes(id));
}

function ShareModal() {
  const { modals, isLeadFileManager, selectedRows, setSelectedRows, setFileManagerFilters, fileManagerFilters } = useFileManagerContext();
  const { showShareFileModal, setShowShareFileModal } = modals;
  const { addToast } = useToast();
  const { fileSharingV2 } = useFileMutations();

  const folderRows = useMemo(() => selectedRows.filter((r) => r.entry_type === 'folder'), [selectedRows]);
  const fileRows = useMemo(() => selectedRows.filter((r) => r.entry_type === 'file'), [selectedRows]);
  const folderCount = folderRows.length;
  const fileCount = fileRows.length;
  const hasFolders = folderCount > 0;
  const hasFiles = fileCount > 0;
  const onlyFolders = hasFolders && !hasFiles;
  const mixedSelection = hasFolders && hasFiles;
  const hasBothTypes = mixedSelection;
  const caseId = selectedRows?.[0]?.case_id ?? '';

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({ defaultValues: { assignee: [] } });

  const watchedAssignee = watch('assignee');

  const [clientOptions, setClientOptions] = useState([]);
  const [sharedContacts, setSharedContacts] = useState([]);
  const [contactsToAdd, setContactsToAdd] = useState([]);
  const [contactsToRemove, setContactsToRemove] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const hasPendingChanges = contactsToAdd.length > 0 || contactsToRemove.length > 0;

  useEffect(() => {
    fetchClients(caseId);
  }, [caseId]);

  useEffect(() => {
    if (selectedRows?.length > 0 && clientOptions?.length > 0) {
      const initial = selectedRows?.reduce((acc, row) => {
        if (row?.file_shared_contact_id) {
          row?.file_shared_contact_id?.forEach((fileContactId) => {
            const found = clientOptions?.find((c) => c?.contact_id === fileContactId);
            if (found) acc?.push({ ...found, display_name: row?.display_name });
          });
        }
        return acc;
      }, []);
      setSharedContacts(initial.map((contact) => ({ ...contact, isPending: false })));
      setContactsToAdd([]);
      setContactsToRemove([]);
    }
  }, [clientOptions, selectedRows]);

  const fetchClients = async (id = '') => {
    try {
      const response = await getFileClients({ case_id: id });
      if (response?.data?.length) {
        setClientOptions(
          response.data.map((element) => ({
            label: generateFullName(element),
            value: element?.client_cognito_username ?? element?.contact_id,
            email: element?.email,
            is_create_client_portal: element?.is_create_client_portal,
            contact_id: element?.contact_id ?? '',
            file_name: element?.display_name ?? '',
          }))
        );
      } else {
        setClientOptions([]);
      }
    } catch {
      setClientOptions([]);
    }
  };

  const inheritedContactIds = useMemo(() => {
    const ids = new Set();
    folderRows?.forEach((folder) => (folder?.file_shared_contact_id ?? []).forEach((id) => ids.add(id)));
    (fileManagerFilters?.shared_contact_list ?? []).forEach((id) => ids.add(id));
    return ids;
  }, [folderRows, fileManagerFilters]);

  const handleAddContacts = useCallback(() => {
    if (!watchedAssignee || watchedAssignee.length === 0) {
      setError('assignee', { type: 'manual', message: 'Select at least one contact to add.' });
      return;
    }
    clearErrors('assignee');

    const nextSharedContacts = [...sharedContacts];
    const nextContactsToAdd = [...contactsToAdd];
    const nextContactsToRemove = [...contactsToRemove];

    watchedAssignee.forEach((contactId) => {
      const option = clientOptions.find((o) => o.value === contactId || o.contact_id === contactId);
      if (!option) return;

      selectedRows.forEach((item) => {
        const itemKey = `${item.pk}#${item.sk}`;
        const alreadyInList = nextSharedContacts.some((c) => isSameContactOnItem(c, item.display_name, option));
        if (alreadyInList) return;

        const removalIndex = nextContactsToRemove.findIndex((r) =>
          isSameContactOnItem({ display_name: r.itemDisplayName, contact_id: r.contactId, value: r.contactId }, item.display_name, option)
        );

        nextSharedContacts.push({
          ...option,
          display_name: item.display_name,
          isPending: removalIndex < 0,
        });

        if (removalIndex >= 0) {
          nextContactsToRemove.splice(removalIndex, 1);
        } else if (!nextContactsToAdd.some((e) => e.itemKey === itemKey && e.contactId === contactId)) {
          nextContactsToAdd.push({ contactId, itemKey, itemDisplayName: item.display_name, item });
        }
      });
    });

    setSharedContacts(nextSharedContacts);
    setContactsToAdd(nextContactsToAdd);
    setContactsToRemove(nextContactsToRemove);
    setValue('assignee', []);
  }, [watchedAssignee, selectedRows, sharedContacts, contactsToAdd, contactsToRemove, clientOptions, setValue, setError, clearErrors]);

  const handleRemoveSharedUser = useCallback((item, contact) => {
    const itemKey = `${item.pk}#${item.sk}`;
    const contactId = contact.contact_id || contact.value;

    setSharedContacts((prev) => prev.filter((c) => !isSameContactOnItem(c, item.display_name, contact)));

    if (contact.isPending) {
      setContactsToAdd((prev) =>
        prev.filter((e) => !(e.itemKey === itemKey && (e.contactId === contact.value || e.contactId === contactId)))
      );
      return;
    }

    setContactsToRemove((prev) => {
      if (prev.some((r) => r.contactId === contactId && r.itemDisplayName === item.display_name)) return prev;
      return [...prev, { contactId, itemDisplayName: item.display_name, item }];
    });
  }, []);

  const handleRemoveAccess = useCallback(
    (item) => {
      const itemKey = `${item.pk}#${item.sk}`;
      const itemContacts = sharedContacts.filter((c) => c.display_name === item.display_name);
      if (!Array.isArray(item.file_shared_contact_id) || item.file_shared_contact_id.length === 0 || !itemContacts.length) return;

      setSharedContacts((prev) => prev.filter((c) => c.display_name !== item.display_name));

      const pendingIds = itemContacts.filter((c) => c.isPending).map((c) => c.value || c.contact_id);
      if (pendingIds.length) {
        setContactsToAdd((prev) => prev.filter((e) => !(e.itemKey === itemKey && pendingIds.includes(e.contactId))));
      }

      const existingContacts = itemContacts.filter((c) => !c.isPending);
      if (existingContacts.length) {
        setContactsToRemove((prev) => {
          const newRemovals = existingContacts
            .filter(
              (contact) =>
                !prev.some((r) =>
                  isSameContactOnItem(
                    { display_name: r.itemDisplayName, contact_id: r.contactId, value: r.contactId },
                    item.display_name,
                    contact
                  )
                )
            )
            .map((contact) => ({
              contactId: contact.contact_id || contact.value,
              itemDisplayName: item.display_name,
              item,
            }));
          return [...prev, ...newRemovals];
        });
      }
    },
    [sharedContacts]
  );

  const handleSaveChanges = useCallback(() => {
    if (!hasPendingChanges) return;

    const hasAdditions = contactsToAdd.length > 0;
    const hasRemovals = contactsToRemove.length > 0;

    let payload;

    if (hasAdditions && !hasRemovals) {
      const shareOps = buildShareOperations(contactsToAdd);
      if (shareOps.length === 1) {
        payload = {
          operation: 'share',
          client_cognito_username: shareOps[0].contacts,
          file_list: shareOps[0].items.file_list,
          folder_list: shareOps[0].items.folder_list,
        };
      } else {
        payload = { share_operations: shareOps };
      }
    } else if (!hasAdditions && hasRemovals) {
      const revokeOps = buildRevokeOperations(contactsToRemove);
      if (revokeOps.length === 1) {
        payload = {
          operation: 'revoke',
          contact_id_list: revokeOps[0].contact_ids,
          file_list: revokeOps[0].items.file_list,
          folder_list: revokeOps[0].items.folder_list,
        };
      } else {
        payload = { revoke_operations: revokeOps };
      }
    } else {
      payload = {
        share_operations: buildShareOperations(contactsToAdd),
        revoke_operations: buildRevokeOperations(contactsToRemove),
      };
    }

    setIsSaving(true);
    fileSharingV2({ case_id: caseId, data: payload, is_lead: isLeadFileManager })
      .then((response) => {
        const message = response?.data?.message || 'Access updated successfully.';
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, message);
        setShowShareFileModal(false);
        setSelectedRows([]);
        setFileManagerFilters((prev) => ({ ...prev, refresh: true }));
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to save changes.');
      })
      .finally(() => setIsSaving(false));
  }, [
    hasPendingChanges,
    contactsToAdd,
    contactsToRemove,
    caseId,
    isLeadFileManager,
    fileSharingV2,
    addToast,
    setShowShareFileModal,
    setSelectedRows,
    setFileManagerFilters,
  ]);

  const handleClose = useCallback(() => setShowShareFileModal(false), [setShowShareFileModal]);

  function checkOptionDisabled(option) {
    return !option.email || !option.is_create_client_portal;
  }

  function ClientsOptionTemplate(e) {
    return (
      <div>
        {e.label} {!e.email ? '( Email address not found )' : !e.is_create_client_portal ? '( Lawft Access Disabled )' : ''}
      </div>
    );
  }

  const getModalTitle = () => {
    if (onlyFolders) return selectedRows.length === 1 ? 'Share Folder' : 'Share Folders';
    if (mixedSelection) return 'Share Items';
    return selectedRows.length === 1 ? 'Share File' : 'Share Files';
  };

  const customFooter = () => (
    <div className="footer-btn d-flex align-items-center justify-content-between flex-wrap gap-2 w-100 gap-2">
      <div className="flex-grow-1">
        {hasPendingChanges && (
          <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
            <i className="fas fa-info-circle m-0" aria-hidden />
            <span className="m-0 text-start">
              You have unsaved changes. Click <strong>Save changes</strong> to apply.
            </span>
          </div>
        )}
      </div>
      <div className="d-flex align-items-center ms-auto">
        <Button label="Cancel" onClick={handleClose} className="p-button p-button-secondary me-3 outline" disabled={isSaving} />
        <Button
          label="Save Changes"
          className="p-button p-button-secondary"
          onClick={handleSaveChanges}
          loading={isSaving}
          disabled={!hasPendingChanges || isSaving}
          autoFocus
        />
      </div>
    </div>
  );

  const renderSharedItem = (item) => {
    const itemContacts = sharedContacts
      .filter((c) => c.display_name === item.display_name)
      .map((c) => ({
        ...c,
        isInherited: !c.isPending && item.entry_type === 'file' && inheritedContactIds.has(c.contact_id || c.value),
      }));
    return (
      <div key={`${item.pk}-${item.sk}`} className="border-top py-3">
        <div className="p-medium d-flex align-items-center justify-content-between shared-file-row gap-2">
          <div className="d-flex align-items-center Sharefile-name-wrap">
            {item.entry_type === 'folder' ? (
              <i className="icon-folder fa-lg me-2" aria-hidden />
            ) : (
              <i className="far fa-file me-2 text-primary" aria-hidden />
            )}

            <span className="d-flex align-items-center">
              <span className="m-0">{item.entry_type === 'folder' ? 'Folder' : 'File'}:</span>

              <span className="ms-1 m-0 break-file-name">{item.display_name}</span>
            </span>
          </div>
          {Array.isArray(item.file_shared_contact_id) && item.file_shared_contact_id.length > 0 && itemContacts.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              className="cursor-pointer remove-access-text"
              onClick={() => handleRemoveAccess(item)}
              onKeyDown={(e) => e.key === 'Enter' && handleRemoveAccess(item)}
            >
              <GlobalTooltip>
                <span className="text-danger">Unshare for all</span>
              </GlobalTooltip>
            </span>
          )}
        </div>
        <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
          {itemContacts.map((assignee) => (
            <Tag
              key={`${assignee.value || assignee.contact_id}-${item.display_name}`}
              className={`tag ${assignee.isPending ? 'tag-green' : 'tag-grey'} fw-normal badge-pill px-2`}
            >
              <span className="text-truncate" style={{ maxWidth: '200px' }}>
                {assignee.label}
              </span>
              {assignee.isInherited && (
                <span className="ms-1 text-muted" style={{ fontSize: '0.7em', whiteSpace: 'nowrap' }}>
                  <i className="icon-folder me-1" aria-hidden />
                  via folder
                </span>
              )}
              <i
                className="pi pi-times-circle ms-2"
                aria-hidden
                onClick={() => handleRemoveSharedUser(item, assignee)}
                style={{ cursor: 'pointer' }}
              />
            </Tag>
          ))}
          {itemContacts.length === 0 && (
            <div className="d-flex align-items-center">
              <i className="icon icon-Alert me-2 F-size16" aria-hidden />
              <span className="text-muted">{item.entry_type === 'folder' ? 'Folder' : 'File'} not shared</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      className="new-task-popup"
      onHide={handleClose}
      header={ModalHeader}
      footer={customFooter}
      title={getModalTitle()}
      visible={showShareFileModal}
      style={{ width: '40vw' }}
    >
      {/* Title row */}
      <div className="mb-3 p-medium">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span className="modal-heading F-size16 text-bold">{getModalTitle()}</span>
          <div className="d-flex align-items-center flex-wrap gap-2">
            {fileCount > 0 && (
              <span className="d-inline-flex align-items-center">
                <i className="far fa-file me-2 text-primary lh-1" aria-hidden />
                <span>
                  {fileCount} File{fileCount !== 1 ? 's' : ''}
                </span>
              </span>
            )}
            {fileCount > 0 && folderCount > 0 && <span className="text-bold">•</span>}
            {folderCount > 0 && (
              <span className="d-inline-flex align-items-center">
                <i className="icon-folder fa-lg me-2 lh-1" aria-hidden />
                <span>
                  {folderCount} Folder{folderCount !== 1 ? 's' : ''}
                </span>
              </span>
            )}
            <span>Selected</span>
          </div>
        </div>
        {hasFolders && (
          <div className="text-muted small mt-2">
            <i className="fas fa-info-circle me-1" aria-hidden />
            Sharing folders will also share all files and subfolders within them.
          </div>
        )}
      </div>

      {/* Share with row */}
      <div className="d-flex align-items-center flex-wrap mb-2">
        <div className="col-lg-3 col-12 p-medium">Share with</div>
        <div className="col-lg-9 col-12 d-flex align-items-start gap-2 select-tag-input">
          <div className="flex-grow-1 min-w-0">
            <Controller
              name="assignee"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  options={clientOptions}
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="Select Contact (s)"
                  className="input-shadow w-100 F-size14"
                  filter
                  id={field.name}
                  value={field.value}
                  optionLabel="label"
                  optionValue="value"
                  display="chip"
                  emptyFilterMessage="No records found"
                  optionDisabled={checkOptionDisabled}
                  itemTemplate={ClientsOptionTemplate}
                />
              )}
            />
            {errors?.assignee && <InputErrorMessage>{errors.assignee.message || 'Required'}</InputErrorMessage>}
          </div>
          <Button
            label="Add"
            className="p-button p-button-secondary"
            style={{ whiteSpace: 'nowrap' }}
            onClick={handleAddContacts}
            type="button"
          />
        </div>
      </div>

      <span className="border-bottom pb-3 w-100 d-block mb-3" />

      {/* SHARED WITH section */}
      <div className="pb-3 w-100 shared-with-section">
        <div className="caption-bold mt-2 mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>
          Shared With
        </div>
        <Card className="card-bg px-2">
          {hasBothTypes ? (
            <>
              {folderRows.map((item) => renderSharedItem(item))}
              {fileRows.map((item) => renderSharedItem(item))}
            </>
          ) : (
            selectedRows.map((item) => renderSharedItem(item))
          )}
        </Card>
      </div>
    </Dialog>
  );
}

export default ShareModal;
